import React from 'react';
import { Brain, Heart } from 'lucide-react';

/**
 * MentalSnapshotCard — chat-inline preview of today's headspace.
 *
 * Renders Mental IQ + latest mood/stress/sleep from `today_checkin`.
 * Empty state (no check-in yet) shows the four mental signals as dashes
 * with a soft prompt — the chip rail below already has a "Log how I'm
 * feeling" CTA.
 */

const mentalBucket = (s) => {
  if (s == null) return null;
  if (s >= 80) return { label: 'thriving',   tone: '#a3e635', note: "You're in a good rhythm — keep what's working." };
  if (s >= 65) return { label: 'steady',     tone: '#CCFF00', note: 'Solid base. A small breathwork session would build on this.' };
  if (s >= 45) return { label: 'mixed',      tone: '#facc15', note: 'Some signals are heavier than others. Be gentle with yourself today.' };
  if (s >= 25) return { label: 'struggling', tone: '#fb923c', note: 'Tough patch — lean into rest, mobility, and reach out if you need to.' };
  return       { label: 'low',               tone: '#f87171', note: 'Heavy week. Skip hard load. Try 5 min of physiological-sigh breathing.' };
};

const Cell = ({ label, value, color }) => (
  <div className="text-center px-1 min-w-0 flex-1">
    <p className="text-[8px] uppercase tracking-wider text-zinc-500 truncate">{label}</p>
    <p className="text-sm font-secondary font-bold capitalize truncate" style={{ color: value ? color : '#52525b' }}>{value || '—'}</p>
  </div>
);

const MOOD_LBL   = { 1: 'rough', 2: 'meh',   3: 'fine',  4: 'good',     5: 'great' };
const STRESS_LBL = { 1: 'calm',  2: 'fine',  3: 'tense', 4: 'high',     5: 'overwhelmed' };
const SLEEP_LBL  = { 1: 'awful', 2: 'poor',  3: 'ok',    4: 'good',     5: 'great' };

const MentalSnapshotCard = ({ data }) => {
  const iq = data?.mental_iq;
  const c = data?.today_checkin;
  const streak = data?.breathwork_streak_days || 0;
  const bucket = mentalBucket(iq);

  return (
    <div className="rounded-sm border border-zinc-700 bg-zinc-900/40 p-4 max-w-md" data-testid="card-mental-snapshot">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Brain className="w-4 h-4 text-accent-purple" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-accent-purple font-secondary font-bold">Readiness snapshot</span>
        </div>
        {iq != null && (
          <div className="text-right" data-testid="snapshot-mental-iq-chat">
            <span className="font-secondary font-bold text-2xl" style={{ color: bucket?.tone }}>{iq}</span>
            <span className="text-[10px] font-secondary font-bold uppercase tracking-wider ml-1.5" style={{ color: bucket?.tone }}>{bucket?.label}</span>
          </div>
        )}
      </div>

      <div className="flex items-stretch gap-2 mb-3">
        <Cell label="Mood"   value={c?.mood   != null ? MOOD_LBL[c.mood]     : null} color="#a855f7" />
        <Cell label="Stress" value={c?.stress != null ? STRESS_LBL[c.stress] : null} color="#a855f7" />
        <Cell label="Focus"  value={c?.focus != null ? `${c.focus}/5` : null} color="#a855f7" />
        <Cell label="Sleep"  value={c?.sleep_quality != null ? SLEEP_LBL[c.sleep_quality] : null} color="#22d3ee" />
      </div>

      {iq != null && bucket?.note ? (
        <p className="text-xs text-zinc-300 leading-relaxed border-l-2 pl-3" style={{ borderColor: bucket.tone }}>{bucket.note}</p>
      ) : (
        <p className="text-xs text-zinc-500 leading-relaxed">
          Tap the chip below to log how you're feeling — your Mental Readiness shows up the moment you submit.
        </p>
      )}

      {streak > 0 && (
        <p className="text-[10px] text-zinc-500 mt-2 flex items-center gap-1">
          <Heart className="w-3 h-3" /> {streak} day breathwork streak
        </p>
      )}
    </div>
  );
};

export default MentalSnapshotCard;
